import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import {
  completeInspection,
  fetchOkkPhotoUrl,
  fetchReceivingPhotoUrl,
  getInspection,
  reopenInspection,
  type InspectionDetail,
  type PhotoView,
  type ReceivingPhotoView,
} from "@/api/okk_receiving";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { authorName, completeBlockers, statusInfo, updText } from "@/lib/okk-receiving";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { CommentForm, CommentList } from "@/pages/okk-receiving/OkkComments";
import { BitrixBlock } from "@/pages/okk-receiving/OkkBitrix";
import { OkkItemCard, type ItemUnsaved } from "@/pages/okk-receiving/OkkItemCard";
import { OkkPhotoBlock } from "@/pages/okk-receiving/OkkPhotos";
import { SamplesTransfersBlock } from "@/pages/okk-receiving/OkkSamples";
import { LazyThumb, StatusBadge, UrlCacheProvider } from "@/pages/order-journal/parts";

/** Что открыто в галерее: фото проверки или фото приёмки (через прокси). */
type Gallery =
  | { kind: "okk"; photos: (PhotoView & { created_at: string })[]; index: number; title: string }
  | { kind: "receiving"; photos: ReceivingPhotoView[]; index: number };

function extOf(contentType: string): string {
  const sub = (contentType.split("/")[1] ?? "").toLowerCase();
  return sub === "jpeg" || !sub ? "jpg" : sub;
}

/**
 * ОКК: карточка проверки прихода — шапка прихода, документы приёмки (скан УПД,
 * сертификаты, товар), позиции с вердиктами и образцами, лента комментариев,
 * общие фото, «Завершить проверку» / «Вернуть в работу».
 */
export function OkkInspectionPage(): React.ReactElement {
  const { inspectionId = "" } = useParams<{ inspectionId: string }>();
  const qc = useQueryClient();
  const goBack = useGoBack("/okk-receiving");
  const key = ["okk-inspection", inspectionId];
  const q = useQuery({ queryKey: key, queryFn: () => getInspection(inspectionId) });
  const [banner, setBanner] = useState("");
  const [gallery, setGallery] = useState<Gallery | null>(null);
  // Что у каких позиций набрано, но не сохранено (сообщают сами карточки).
  const [unsaved, setUnsaved] = useState<Record<string, ItemUnsaved>>({});
  const reportUnsaved = useCallback((itemId: string, state: ItemUnsaved | null) => {
    setUnsaved((prev) => {
      const cur = prev[itemId];
      if (!state) {
        if (!cur) return prev;
        const rest = { ...prev };
        delete rest[itemId];
        return rest;
      }
      if (cur && cur.verdict === state.verdict && cur.samples === state.samples) return prev;
      return { ...prev, [itemId]: state };
    });
  }, []);

  const invalidateLists = () => {
    void qc.invalidateQueries({ queryKey: ["okk-inspections"] });
  };
  // Свежий ответ PATCH/POST не должен потерять гонку с GET, начатым до него:
  // сначала отменяем летящее чтение, потом кладём ответ в кеш.
  const put = async (detail: InspectionDetail) => {
    await qc.cancelQueries({ queryKey: key });
    qc.setQueryData(key, detail);
    invalidateLists();
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    invalidateLists();
  };

  const complete = useMutation({
    mutationFn: () => completeInspection(inspectionId),
    onMutate: () => setBanner(""),
    onSuccess: put,
    onError: (e) => setBanner(apiErrorText(e)),
  });
  const reopen = useMutation({
    mutationFn: () => reopenInspection(inspectionId),
    onMutate: () => setBanner(""),
    onSuccess: put,
    onError: (e) => setBanner(apiErrorText(e)),
  });

  const d = q.data;
  const done = d?.status === "done";
  const blockers = d ? completeBlockers(d.items) : [];
  const unsavedItems = d ? d.items.filter((i) => unsaved[i.id]) : [];

  const openOkk = (photos: PhotoView[], index: number, title: string) =>
    setGallery({
      kind: "okk",
      photos: photos.map((p) => ({ ...p, created_at: p.uploaded_at })),
      index,
      title,
    });

  return (
    <UrlCacheProvider>
      <div className="mx-auto max-w-6xl space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={goBack}
            className="flex size-9 shrink-0 items-center justify-center rounded-md border hover:bg-accent"
            aria-label="К списку проверок"
          >
            <ArrowLeft className="size-4" />
          </button>
          <h1 className="min-w-0 flex-1 break-words text-xl font-semibold">
            ОКК: {d?.delivery.kontragent_name || "проверка прихода"}
          </h1>
          {d && <StatusBadge tone={statusInfo(d.status).tone}>{statusInfo(d.status).text}</StatusBadge>}
        </div>

        {!d ? (
          <LoadState
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            className="flex flex-col items-center justify-center gap-3 py-16 text-center"
          />
        ) : (
          <>
            {q.isError && (
              <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                Карточка не обновилась: {apiErrorText(q.error)}
              </div>
            )}
            <Header detail={d} />

            <Card className="space-y-2 p-3">
              {done ? (
                <>
                  <div className="text-sm">
                    Проверка завершена{d.completed_by ? ` · ${authorName(d.completed_by)}` : ""}
                    {d.completed_at ? ` · ${fmtDateTime(d.completed_at)}` : ""}. Чтобы менять
                    вердикты и фото, верните её в работу.
                  </div>
                  <Button
                    variant="outline"
                    disabled={reopen.isPending}
                    onClick={() => reopen.mutate()}
                  >
                    {reopen.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                    Вернуть в работу
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    disabled={blockers.length > 0 || unsavedItems.length > 0 || complete.isPending}
                    onClick={() => complete.mutate()}
                    className="w-full sm:w-auto"
                  >
                    {complete.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
                    Завершить проверку
                  </Button>
                  {blockers.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Завершить можно, когда у всех позиций есть вердикт и нет «на тесте» —
                      сейчас {blockers.join(", ")}
                    </div>
                  )}
                  {unsavedItems.length > 0 && (
                    <div className="text-xs text-amber-800" data-testid="okk-unsaved-hint">
                      Есть несохранённые изменения у позиции:{" "}
                      {unsavedItems.map((i) => i.nomenclature_name).join(", ")}
                    </div>
                  )}
                </>
              )}
              {banner && (
                <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                  {banner}
                </div>
              )}
            </Card>

            <BitrixBlock detail={d} onSynced={put} onReread={refresh} />

            <ReceivingDocs
              inspectionId={d.id}
              photos={d.receiving_photos}
              onOpen={(index) => setGallery({ kind: "receiving", photos: d.receiving_photos, index })}
            />

            <section aria-label="Позиции" className="space-y-2">
              <h2 className="text-base font-semibold">
                Позиции ({d.items.filter((i) => !i.removed_from_delivery).length})
              </h2>
              {d.items.length === 0 && (
                <p className="text-sm text-muted-foreground">Позиций для ОКК в приходе нет</p>
              )}
              {d.items.map((item) => {
                const photos = d.photos.filter((p) => p.item_id === item.id);
                return (
                  <OkkItemCard
                    key={item.id}
                    inspectionId={d.id}
                    item={item}
                    transfer={
                      d.samples_transfers.find((t) => t.id === item.samples_transfer_id) ?? null
                    }
                    done={done}
                    photos={photos}
                    comments={d.comments.filter((c) => c.item_id === item.id)}
                    onSaved={put}
                    onChanged={refresh}
                    onOpenPhoto={(i) => openOkk(photos, i, item.nomenclature_name)}
                    onUnsaved={reportUnsaved}
                  />
                );
              })}
            </section>

            <SamplesTransfersBlock
              detail={d}
              onSaved={put}
              onChanged={refresh}
              busyItems={unsavedItems.filter((i) => unsaved[i.id]?.samples).map((i) => i.nomenclature_name)}
            />

            <div className="grid gap-3 lg:grid-cols-2">
              <Card className="space-y-2 p-3">
                <h2 className="text-base font-semibold">Комментарии ({d.comments.length})</h2>
                <CommentList
                  inspectionId={d.id}
                  comments={d.comments}
                  items={d.items}
                  onChanged={refresh}
                />
                <CommentForm inspectionId={d.id} items={d.items} onAdded={refresh} />
              </Card>
              <Card className="space-y-2 p-3">
                <h2 className="text-base font-semibold">Общие фото проверки</h2>
                {(() => {
                  const general = d.photos.filter((p) => p.item_id === null);
                  return (
                    <OkkPhotoBlock
                      inspectionId={d.id}
                      itemId={null}
                      title="Фото проверки"
                      photos={general}
                      locked={done}
                      onOpen={(i) => openOkk(general, i, "Фото проверки")}
                      onChanged={refresh}
                    />
                  );
                })()}
                {done && (
                  <p className="text-xs text-muted-foreground">
                    Проверка завершена — фото добавляются после «Вернуть в работу».
                  </p>
                )}
              </Card>
            </div>
          </>
        )}
      </div>

      {gallery?.kind === "okk" && (
        <PhotoLightbox
          photos={gallery.photos}
          index={gallery.index}
          loadUrl={(p) => fetchOkkPhotoUrl(p.id, false)}
          title={() => gallery.title}
          caption={(p) => `${fmtDateTime(p.uploaded_at)} · ${authorName(p.uploaded_by)}`}
          fileName={(p, i) => `ОКК ${gallery.title} (${i + 1}).${extOf(p.content_type)}`}
          onNavigate={(index) => setGallery({ ...gallery, index })}
          onClose={() => setGallery(null)}
        />
      )}
      {gallery?.kind === "receiving" && d && (
        <PhotoLightbox
          photos={gallery.photos}
          index={gallery.index}
          loadUrl={(p) => fetchReceivingPhotoUrl(d.id, p.id, false)}
          title={(p) => receivingPhotoTitle(p)}
          fileName={(p, i) => `Приёмка ${receivingPhotoTitle(p)} (${i + 1}).jpg`}
          onNavigate={(index) => setGallery({ ...gallery, index })}
          onClose={() => setGallery(null)}
        />
      )}
    </UrlCacheProvider>
  );
}

function receivingPhotoTitle(p: ReceivingPhotoView): string {
  return p.place_seq ? `${p.category_title} · место ${p.place_seq}` : p.category_title;
}

function Header({ detail }: { detail: InspectionDetail }): React.ReactElement {
  const d = detail.delivery;
  return (
    <Card className="grid gap-x-6 gap-y-1 p-3 text-sm sm:grid-cols-2 sm:p-4">
      <Field label="Поставщик">{d.kontragent_name || "—"}</Field>
      <Field label="Организация">{d.organization_name || "—"}</Field>
      <Field label="Заказ поставщику">{d.order_number || "—"}</Field>
      <Field label="УПД">
        <span className={d.upd_missing ? "text-amber-700" : undefined}>{updText(d)}</span>
      </Field>
      <Field label="Приходная накладная">
        {d.one_c_receipt_number
          ? `${d.one_c_receipt_number} · ${d.receipt_posted ? "проведена" : "не проведена"}`
          : "нет"}
      </Field>
      <Field label="Поступление по заказу">
        {d.receiving_date ? fmtDate(d.receiving_date) : "—"}
        <span className="text-muted-foreground"> · плановая дата заказа</span>
      </Field>
      <Field label="Приёмка завершена">{d.completed_at ? fmtDateTime(d.completed_at) : "—"}</Field>
      <Field label="Проверка заведена">
        {fmtDateTime(detail.created_at)}
        {detail.overdue === true && (
          <span className="ml-2 font-medium text-rose-700" data-testid="okk-overdue">
            просрочена
          </span>
        )}
      </Field>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex gap-2">
      <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/** Документы приёмки: фото, снятые кладовщиком (скан УПД, сертификаты, товар),
 *  по категориям — только просмотр, через прокси раздела ОКК. */
function ReceivingDocs({
  inspectionId,
  photos,
  onOpen,
}: {
  inspectionId: string;
  photos: ReceivingPhotoView[];
  onOpen: (index: number) => void;
}): React.ReactElement {
  const categories = [...new Set(photos.map((p) => p.category_title))];
  return (
    <Card className="space-y-2 p-3">
      <h2 className="text-base font-semibold">Документы приёмки</h2>
      {photos.length === 0 && (
        <p className="text-sm text-muted-foreground">Кладовщик фото приёмки не снимал</p>
      )}
      {categories.map((cat) => (
        <div key={cat} className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">{cat}</div>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) =>
              p.category_title === cat ? (
                <LazyThumb
                  key={p.id}
                  cacheKey={`rcv-thumb:${p.id}`}
                  load={() => fetchReceivingPhotoUrl(inspectionId, p.id, true)}
                  onClick={() => onOpen(i)}
                  label={receivingPhotoTitle(p)}
                />
              ) : null,
            )}
          </div>
        </div>
      ))}
    </Card>
  );
}
